const en: Record<string, string> = {
  // ── App ──────────────────────────────────────────────────────────────────
  'app.title': 'AI Translation Risk Assessment',

  // ── Login ────────────────────────────────────────────────────────────────
  'login.title': 'AI Translation Risk Assessment',
  'login.subtitle': 'Translation Risk Assessment Portal',
  'login.description': 'Sign in with your corporate credentials to run translation risk assessments.',
  'login.ssoButton': 'Sign in with SSO',
  'login.footer': 'Secured by Zitadel · OAuth 2.1',

  // ── Header ───────────────────────────────────────────────────────────────
  'header.title': 'AI Translation Risk Assessment',
  'header.home': 'Home',
  'header.avatarLabel': 'User menu',
  'header.languageLabel': 'Select language',
  'header.admin': 'Configuration',
  'header.signOut': 'Sign out',

  // ── Theme ────────────────────────────────────────────────────────────────
  'theme.toDark': 'Switch to dark theme',
  'theme.toLight': 'Switch to light theme',

  // ── Auth ─────────────────────────────────────────────────────────────────
  'auth.callback.loading': 'Signing you in…',
  'accessDenied.title': 'Access denied',
  'accessDenied.body':
    'You are signed in, but your account is not granted access. Contact your administrator if you believe this is a mistake.',

  // ── Run Assessment ──────────────────────────────────────────────────────
  'assessment.title': 'Run assessment',
  'assessment.subtitle': 'Upload a file or paste text to run a translation risk assessment. Your administrator sets the risk factors.',
  'assessment.uploadCard.title': 'Upload & run',
  'assessment.resultCard.title': 'Progress & result',
  'assessment.tab.file': 'Upload file',
  'assessment.tab.text': 'Enter text',
  'assessment.upload.dropzone.title': 'Drop file here',
  'assessment.upload.dropzone.browse': 'or Browse files',
  'assessment.upload.dropzone.formats': 'Supports {formats}',
  'assessment.upload.remove': 'Remove file',
  'assessment.upload.unsupported': '“{name}” is not a supported format. Use .xlf, .xliff or .txt.',
  'assessment.upload.text.placeholder': 'Insert or type text for risk assessment…',
  'assessment.upload.text.tooLong': 'The text exceeds the {max} character limit. Please shorten it.',
  'assessment.upload.charCount': '{count} / {max} characters',
  'assessment.sourceLanguage': 'Source language (optional)',
  'assessment.targetLanguage': 'Target language (optional)',
  // Language-picker placeholders (shared LanguagePicker component)
  'upload.sourceLanguage.placeholder': 'Select source language',
  'upload.targetLanguages.placeholder': 'Select target language',
  'assessment.runButton': 'Run assessment',
  'assessment.running': 'Running…',
  // Result panel
  'assessment.result.idle': 'Idle',
  'assessment.result.empty.title': 'No assessment yet',
  'assessment.result.empty.body': 'To view the aggregated risk score and per-factor report, add your content under “Upload & run”, then select Run assessment.',
  'assessment.result.inProgress': 'In progress',
  'assessment.result.assessing': 'Assessing your content…',
  'assessment.result.assessing.hint': 'This usually takes less than a minute. You can keep this tab open — we’ll update it automatically.',
  'assessment.progress.stage.domain': 'Classifying the content domain…',
  'assessment.progress.stage.risk': 'Scanning for sensitive or high-risk content…',
  'assessment.progress.stage.format': 'Checking formatting and structural integrity…',
  'assessment.progress.stage.difficulty': 'Estimating translation difficulty…',
  'assessment.progress.stage.scoring': 'Aggregating factor scores…',
  'assessment.result.completed': 'Completed',
  'assessment.result.failed': 'Failed',
  'assessment.result.id': 'Assessment {id}',
  'assessment.result.noReport': 'The assessment completed but returned no risk report.',
  // Risk report
  'assessment.report.aggregatedScore': 'Aggregated risk score',
  'assessment.report.highRisk': 'High risk',
  'assessment.report.lowRisk': 'Low risk',
  'assessment.report.notClassified': 'Not classified',
  'assessment.report.borderline': 'Borderline',
  'assessment.report.borderlineHint':
    'The score is close to the decision boundary, so this verdict could have gone either way.',
  'assessment.report.factors': 'Factors',
  'assessment.report.score': 'Score',
  'assessment.report.weight': 'Weight',
  'assessment.report.veto': 'Veto triggered',
  'assessment.report.failed': 'failed',
  // Errors
  'assessment.error.start': 'Could not start the assessment. Please try again.',
  'assessment.error.tooLarge': 'The file exceeds the {max} upload limit. Please choose a smaller file.',
  'assessment.error.poll': 'Lost connection while waiting for the result. Please try again.',
  'assessment.error.failed': 'The assessment failed.',

  // ── Admin ───────────────────────────────────────────────────────────────
  'admin.title': 'Configuration',
  'admin.subtitle': 'Manage the LLM connector and risk factors for your organisation.',
  'admin.saved': 'Configuration saved.',
  'admin.saveError': 'Could not save. Please try again.',
  // Configuration page shell — sider nav, external tools, save bar
  'admin.retry': 'Retry',
  'admin.nav.label': 'Configuration sections',
  'admin.nav.unsaved': 'has unsaved changes',
  'admin.nav.connector': 'LLM connector',
  'admin.nav.factorConfig': 'Risk factors',
  'admin.nav.customFactors': 'Custom factors',
  'admin.tools.mtLlmGateway': 'XTM Gateway',
  'admin.tools.newTab': '(opens in a new tab)',
  'admin.footer.unsaved': 'Unsaved changes',
  'admin.footer.save': 'Save changes',
  'admin.footer.discard': 'Discard',
  // Connector
  'admin.connector.title': 'LLM connector',
  'admin.connector.subtitle': 'Select the default LLM connector for users in your organisation.',
  'admin.connector.label': 'Default LLM connector for your organisation',
  'admin.connector.placeholder': 'Select a connector',
  'admin.connector.saved': 'Connector configuration saved.',
  'admin.connector.none': 'No default connector configured yet.',
  'admin.connector.unavailable': 'Unavailable connector',
  'admin.connector.missing': 'The saved connector is no longer available. It may have been disabled or deleted. Choose another connector.',
  'admin.connector.loadError': 'Could not load the connector configuration.',
  'admin.connector.override': 'All risk assessments use the default connector unless a user specifies in their request the ID of another connector enabled for your organisation.',
  // Factor config
  'admin.factorConfig.title': 'Risk factor configuration',
  'admin.factorConfig.subtitle': 'The LLM calculates the risk score from the risk factors. Adjust the standard factor weights here; configure your own in the Custom factors section.',
  'admin.factorConfig.loadError': 'Could not load the factor configuration.',
  // One column, not two: the factor's description *is* its label. The API's
  // camelCase factorKey moved to the cell's title attribute.
  'admin.factorConfig.col.factor': 'Factor',
  'admin.factorConfig.col.enabled': 'Enabled',
  'admin.factorConfig.col.weight': 'Weight',
  'admin.factorConfig.col.veto': 'Veto',
  'admin.factorConfig.tip.weight': 'Impact on the total risk score compared to other enabled factors.',
  'admin.factorConfig.tip.veto': 'A factor’s score at the set value flags the assessment as high-risk (veto), regardless of the aggregated result.',
  // Per-row accessible names. The column header does not name a control inside a
  // data cell, so without these every input reads as an unlabelled spinbutton.
  'admin.factorConfig.enabledFor': 'Enable {factor}',
  'admin.factorConfig.weightFor': 'Weight for {factor}',
  'admin.factorConfig.vetoFor': 'Veto threshold for {factor}',
  'admin.factorConfig.error.empty': 'Load the configuration first.',
  'admin.factorConfig.error.weight': 'Every weight must be greater than 0.',
  'admin.factorConfig.error.veto': 'Every veto threshold must be in the range (0, 1].',
  'admin.factorConfig.error.noneEnabled': 'At least one factor must be enabled.',
  // Custom factors — list
  'admin.customFactors.title': 'Custom risk factors',
  'admin.customFactors.tip': 'Configure custom risk factors. The LLM assesses these alongside the enabled standard risk factors, using the Instruction field value as a prompt.',
  'admin.customFactors.usage': '{count} / {max} used',
  // No '+' in the copy: the button carries a Plus icon, as every other add
  // control in this feature already does.
  'admin.customFactors.new': 'New factor',
  'admin.customFactors.empty': 'No custom factors yet. Add one to score something the standard factors do not cover.',
  'admin.customFactors.loadError': 'Could not load custom factors.',
  'admin.customFactors.col.name': 'Custom factor name',
  'admin.customFactors.col.enabled': 'Enabled',
  'admin.customFactors.col.weight': 'Weight',
  'admin.customFactors.col.veto': 'Veto',
  'admin.customFactors.col.actions': 'Actions',
  'admin.customFactors.enabledFor': 'Enable {name}',
  'admin.customFactors.edit': 'Edit',
  'admin.customFactors.delete': 'Delete',
  'admin.customFactors.limit': 'You have reached the limit of 6 custom factors for your organisation. Delete one before creating another.',
  'admin.customFactors.deleteTitle': 'Delete custom factor?',
  'admin.customFactors.deleteBody': 'Delete “{name}”? This action cannot be undone.',
  'admin.customFactors.deleteConfirm': 'Delete',
  'admin.customFactors.leaveTitle': 'Discard this custom factor?',
  'admin.customFactors.leaveBody':
    'You have an unfinished custom factor. Leaving this section will discard everything you have entered.',
  'admin.customFactors.leaveConfirm': 'Discard and leave',
  'admin.customFactors.leaveCancel': 'Keep editing',
  'admin.customFactors.switchTitle': 'Save your changes?',
  'admin.customFactors.switchBody': 'You have unsaved changes to “{current}”. Save or discard them to edit “{next}”.',
  'admin.customFactors.switchBodyNew': 'You have an unsaved new custom factor. Save or discard it to edit “{next}”.',
  'admin.customFactors.switchSave': 'Save and continue',
  'admin.customFactors.switchDiscard': 'Discard changes',
  'admin.customFactors.switchCancel': 'Keep editing',
  // Custom factors — form
  'admin.customFactor.createTitle': 'Create custom factor',
  'admin.customFactor.editTitle': 'Edit custom factor',
  'admin.customFactor.displayName': 'Custom factor name',
  'admin.customFactor.description': 'Description',
  'admin.customFactor.instruction': 'Instruction',
  'admin.customFactor.instruction.tip': 'The LLM uses these instructions as a prompt to score this factor.',
  'admin.customFactor.instruction.placeholder': 'Example: Evaluate whether the translation preserves the brand’s formal tone…',
  'admin.customFactor.bands': 'Scoring bands',
  'admin.customFactor.bands.min': 'Min',
  'admin.customFactor.bands.max': 'Max',
  'admin.customFactor.bands.description': 'Description',
  'admin.customFactor.bands.add': 'Add band',
  'admin.customFactor.bands.remove': 'Remove band',
  'admin.customFactor.enabled': 'Enabled',
  'admin.customFactor.weight': 'Weight',
  'admin.customFactor.veto': 'Veto threshold',
  'admin.customFactor.create': 'Create custom factor',
  'admin.customFactor.update': 'Update custom factor',
  'admin.customFactor.cancel': 'Cancel',
  'admin.customFactor.deleteError': 'Could not delete the custom factor.',
  // Custom factor / band validation (returned by utils/scoringBands)
  'admin.customFactor.error.displayName': 'Custom factor name is required.',
  'admin.customFactor.error.description': 'Description is required.',
  'admin.customFactor.error.instruction': 'Instruction is required.',
  'admin.customFactor.error.forbiddenToken': 'Instruction must not contain the reserved token <translation_segments>.',
  'admin.customFactor.error.weight': 'Weight must be greater than 0.',
  'admin.customFactor.error.veto': 'Veto threshold must be in the range (0, 1].',
  'admin.customFactor.bands.tooFew': 'At least 2 scoring bands are required.',
  'admin.customFactor.bands.tooMany': 'At most 10 scoring bands are allowed.',
  'admin.customFactor.bands.blankDescription': 'Each scoring band needs a description.',
  'admin.customFactor.bands.forbiddenToken': 'Band descriptions must not contain <translation_segments>.',
  'admin.customFactor.bands.range': 'Each band must have min < max, within [0, 1].',
  'admin.customFactor.bands.start': 'The first scoring band must start at 0.',
  'admin.customFactor.bands.end': 'The last scoring band must end at 1.',
  'admin.customFactor.bands.gap': 'Scoring bands must be contiguous — no gaps.',
  'admin.customFactor.bands.overlap': 'Scoring bands must not overlap.',

  // ── Shared ───────────────────────────────────────────────────────────────
  'shared.loading': 'Loading…',
  'shared.error.title': 'Something went wrong',
  'shared.error.description': 'An unexpected error occurred. Please try refreshing the page.',
  'shared.error.retry': 'Try again',
  'shared.empty': 'No data available',
};

export default en;
